import React from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  Sheet,
  Chip,
} from '@mui/joy'
import Grid from '@mui/joy/Grid'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from 'recharts'
import { useSalesPipeline } from '../../../hooks/useSalesReports'
import type { DateRange } from '../../../types/salesReports'

interface PipelineAnalyticsProps {
  dateRange: DateRange
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

const STAGE_COLORS = ['#0B6BCB', '#1E88E5', '#42A5F5', '#64B5F6', '#90CAF9', '#BBDEFB']
const LOSS_COLORS = ['#D32F2F', '#E57373', '#EF9A9A', '#FFCDD2', '#F9A825', '#FFE082']

export default function PipelineAnalytics({ dateRange }: PipelineAnalyticsProps) {
  const { data, isLoading, error } = useSalesPipeline({
    date_from: dateRange.from,
    date_to: dateRange.to,
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert color="danger">
        Failed to load pipeline data. Please try again.
      </Alert>
    )
  }

  if (!data) {
    return (
      <Alert color="warning">
        No data available for the selected period.
      </Alert>
    )
  }

  const { stages, conversion_rates, forecast, loss_reasons } = data

  // Filter out closed stages for funnel visualization
  const openStages = stages.filter(s => !s.stage.includes('closed'))

  // Prepare funnel data
  const funnelData = openStages.map((stage, index) => ({
    name: stage.stage_display,
    value: stage.total_value,
    count: stage.count,
    fill: STAGE_COLORS[index % STAGE_COLORS.length],
  }))

  return (
    <Box>
      <Typography level="title-lg" sx={{ mb: 3 }}>
        Pipeline Analytics
      </Typography>

      {/* Stage Distribution */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid xs={12} lg={8}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Pipeline by Stage
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={openStages} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(val) => formatCurrency(val)} />
                  <YAxis dataKey="stage_display" type="category" width={100} />
                  <Tooltip
                    formatter={(value, name) => [
                      name === 'total_value' ? formatCurrency(value as number) : value,
                      name === 'total_value' ? 'Value' : name === 'count' ? 'Count' : name
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="total_value" fill="#0B6BCB" name="Value" />
                  <Bar dataKey="weighted_value" fill="#06A77D" name="Weighted" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} lg={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Stage Details
              </Typography>
              <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                <Table size="sm">
                  <thead>
                    <tr>
                      <th>Stage</th>
                      <th style={{ textAlign: 'right' }}>Count</th>
                      <th style={{ textAlign: 'right' }}>Value</th>
                      <th style={{ textAlign: 'right' }}>Avg Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stages.map((stage) => (
                      <tr key={stage.stage}>
                        <td>{stage.stage_display}</td>
                        <td style={{ textAlign: 'right' }}>{stage.count}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(stage.total_value)}</td>
                        <td style={{ textAlign: 'right' }}>{stage.avg_days_in_stage}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Conversion Rates */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid xs={12} lg={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Stage Conversion Rates
              </Typography>
              {conversion_rates.length > 0 ? (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                  <Table>
                    <thead>
                      <tr>
                        <th>From</th>
                        <th>To</th>
                        <th style={{ textAlign: 'right' }}>Entered</th>
                        <th style={{ textAlign: 'right' }}>Converted</th>
                        <th style={{ textAlign: 'right' }}>Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conversion_rates.map((cr) => (
                        <tr key={`${cr.from_stage}-${cr.to_stage}`}>
                          <td>{cr.from_display}</td>
                          <td>{cr.to_display}</td>
                          <td style={{ textAlign: 'right' }}>{cr.entered}</td>
                          <td style={{ textAlign: 'right' }}>{cr.converted}</td>
                          <td style={{ textAlign: 'right' }}>
                            <Chip
                              size="sm"
                              variant="soft"
                              color={cr.rate >= 50 ? 'success' : cr.rate >= 25 ? 'warning' : 'danger'}
                            >
                              {cr.rate}%
                            </Chip>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Sheet>
              ) : (
                <Alert color="neutral">No conversion data available for this period</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Forecast */}
        <Grid xs={12} lg={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Pipeline Forecast
              </Typography>
              <Grid container spacing={2}>
                <Grid xs={12} sm={4}>
                  <Card variant="soft" color="success">
                    <CardContent>
                      <Typography level="body-xs">30 Days</Typography>
                      <Typography level="h4">{formatCurrency(forecast['30_days'].weighted_value)}</Typography>
                      <Typography level="body-xs">{forecast['30_days'].count} deals</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid xs={12} sm={4}>
                  <Card variant="soft" color="primary">
                    <CardContent>
                      <Typography level="body-xs">60 Days</Typography>
                      <Typography level="h4">{formatCurrency(forecast['60_days'].weighted_value)}</Typography>
                      <Typography level="body-xs">{forecast['60_days'].count} deals</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid xs={12} sm={4}>
                  <Card variant="soft" color="warning">
                    <CardContent>
                      <Typography level="body-xs">90 Days</Typography>
                      <Typography level="h4">{formatCurrency(forecast['90_days'].weighted_value)}</Typography>
                      <Typography level="body-xs">{forecast['90_days'].count} deals</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <Typography level="body-xs" sx={{ mt: 2, color: 'text.tertiary' }}>
                Forecast based on weighted pipeline value (expected close date)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Loss Reasons */}
      {loss_reasons.length > 0 && (
        <Grid container spacing={3}>
          <Grid xs={12} lg={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" sx={{ mb: 2 }}>
                  Loss Reasons
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={loss_reasons as any[]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry: any) => `${entry.reason_display}: ${entry.count}`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="count"
                      nameKey="reason_display"
                    >
                      {loss_reasons.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={LOSS_COLORS[index % LOSS_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, _name, props) => [
                        `${value} deals (${formatCurrency((props.payload as any).total_value)})`,
                        (props.payload as any).reason_display
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} lg={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" sx={{ mb: 2 }}>
                  Loss Analysis
                </Typography>
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                  <Table>
                    <thead>
                      <tr>
                        <th>Reason</th>
                        <th style={{ textAlign: 'right' }}>Deals Lost</th>
                        <th style={{ textAlign: 'right' }}>Value Lost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loss_reasons.map((lr) => (
                        <tr key={lr.reason}>
                          <td>{lr.reason_display}</td>
                          <td style={{ textAlign: 'right' }}>{lr.count}</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(lr.total_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Sheet>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  )
}
